import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Languages, Shield, MapPin, Database, Mail, Bug, Server, CreditCard } from 'lucide-react';

// SHARED-4 / CLINIO-194 deliverable 5 — public sub-processor list for v2.clinio.app.
//
// Public, unauthenticated, bilingual (da-DK / en-DK). No clinic-specific data;
// no patient data. Updated when a sub-processor is added or removed (the
// publication of an updated list is the 30-day notice mechanism committed to
// in the DPAs. DPA boilerplate lives in docs/archive/legal/ (local only, gitignored).
//
// Edit this file (not a CMS) so the public list is git-tracked and reviewable.

type Locale = 'da' | 'en';

type SubProcessor = {
  id: string;
  name: string;
  parent_entity: string;
  service: { da: string; en: string };
  data: { da: string; en: string };
  country: string; // ISO 3166-1 alpha-2 of processing location
  country_label: { da: string; en: string };
  legal_basis: { da: string; en: string };
  systems: Array<'v2' | 'pay' | 'forms'>;
  url?: string;
};

const LAST_REVIEWED = '2026-10-22';

const COPY = {
  page_title: {
    da: 'Underdatabehandlere',
    en: 'Sub-processors',
  },
  page_subtitle: {
    da: 'Tredjeparter, der behandler personoplysninger på vegne af Clinio',
    en: 'Third parties that process personal data on behalf of Clinio',
  },
  last_reviewed: {
    da: 'Senest gennemgået',
    en: 'Last reviewed',
  },
  preamble: {
    da:
      'Denne side opregner alle aktuelle underdatabehandlere, der behandler personoplysninger ' +
      'om Clinio v2-klinikker, deres patienter eller deres slutbrugere. Listen er den autoritative ' +
      'reference for vores GDPR Art. 28 databehandleraftaler (DPAs). Ved tilføjelse eller udskiftning ' +
      'af en underdatabehandler giver vi mindst 30 dages forudgående varsel ved opdatering af denne side.',
    en:
      'This page lists every current sub-processor that processes personal data about Clinio v2 ' +
      'clinics, their patients, or their end users. The list is the authoritative reference for our ' +
      'GDPR Art. 28 Data Processing Agreements (DPAs). When we add or replace a sub-processor we give ' +
      'at least 30 days\u2019 prior notice by updating this page.',
  },
  hard_rules_title: {
    da: 'Hårde dataregler (uændrede på tværs af alle underdatabehandlere)',
    en: 'Hard data rules (unchanged across every sub-processor)',
  },
  hard_rules: {
    da: [
      'CPR-numre forlader aldrig Clinio v2-databasen — hverken til Clinio Pay, Clinio Forms eller en underdatabehandler.',
      'Patientens navn og e-mail må kun krydse til Clinio Forms (for at adressere spørgeskemaerne). Aldrig til Clinio Pay.',
      'Telefonnummer og adresse krydser aldrig nogen systemgrænse.',
      'Frie klinisk noter (journal, aftalenoter, diagnose) krydser aldrig nogen systemgrænse.',
      'Rå spørgeskema-svar bliver i Clinio Forms; kun den strukturerede score krydser til Clinio v2.',
      'Alle systemer er hostet i EU (Tyskland og Irland). Se ',
    ],
    en: [
      'CPR (Danish national identity numbers) never leaves the Clinio v2 database \u2014 not to Clinio Pay, not to Clinio Forms, not to any sub-processor.',
      'Patient name and email may only cross to Clinio Forms (to address the questionnaire). Never to Clinio Pay.',
      'Phone number and address never cross any system boundary.',
      'Free-text clinical notes (journal, appointment notes, diagnosis) never cross any system boundary.',
      'Raw questionnaire answers stay in Clinio Forms; only the structured score crosses to Clinio v2.',
      'All systems are hosted in the EU (Germany and Ireland). See ',
    ],
  },
  residency_link: {
    da: 'EU-residenstabellen',
    en: 'the EU residency table',
  },
  for_the_full_list: {
    da: 'for verifikationsdetaljer.',
    en: 'for verification details.',
  },
  table_header: {
    sub_processor: { da: 'Underdatabehandler', en: 'Sub-processor' },
    service: { da: 'Tjeneste', en: 'Service' },
    data: { da: 'Data behandlet', en: 'Data processed' },
    country: { da: 'Land', en: 'Country' },
    legal_basis: { da: 'Retsgrundlag / overførsel', en: 'Legal basis / transfer' },
    systems: { da: 'System', en: 'System' },
  },
  filter_label: {
    da: 'Filtrér efter system',
    en: 'Filter by system',
  },
  filter_all: { da: 'Alle systemer', en: 'All systems' },
  systems_label: {
    v2: { da: 'Clinio v2 (CRM)', en: 'Clinio v2 (CRM)' },
    pay: { da: 'Clinio Pay (Betalinger)', en: 'Clinio Pay (Payments)' },
    forms: { da: 'Clinio Forms (Spørgeskemaer)', en: 'Clinio Forms (Questionnaires)' },
  },
  contact_title: {
    da: 'Kontakt vedrørende databeskyttelse',
    en: 'Data protection contact',
  },
  contact_body: {
    da: 'Spørgsmål om denne liste eller indsigelser mod en underdatabehandler kan sendes til ',
    en: 'Questions about this list or objections to a sub-processor may be sent to ',
  },
  contact_email: 'dpo@clinio.dk',
  archive_title: {
    da: 'Historik',
    en: 'Change history',
  },
  archive_body: {
    da:
      'Ændringer i denne liste spores via git-historikken i ' +
      'web/src/pages/legal/SubProcessors.tsx. Tidligere versioner kan tilgås via en Clinio-administrator efter anmodning.',
    en:
      'Changes to this list are tracked via the git history of ' +
      'web/src/pages/legal/SubProcessors.tsx. Prior versions are available from a Clinio administrator on request.',
  },
};

const SUB_PROCESSORS: SubProcessor[] = [
  {
    id: 'supabase',
    name: 'Supabase Inc.',
    parent_entity: 'Supabase Inc. (US-Singapore parent; EU operations via Supabase Ireland)',
    service: {
      da:
        'Hostet PostgreSQL-database, Edge Functions-runtime, Storage og Auth ' +
        'for alle tre Clinio-systemer (separate projekter pr. system).',
      en:
        'Hosted PostgreSQL database, Edge Functions runtime, Storage and Auth ' +
        'for all three Clinio systems (separate projects per system).',
    },
    data: {
      da:
        'Alle persondata behandlet af Clinio: krypteret CPR, patientstamdata, ' +
        'aftaler, fakturaer, journalnotater, dokumenter, SMS-logs, spørgeskemasvar.',
      en:
        'All personal data processed by Clinio: encrypted CPR, patient master data, ' +
        'appointments, invoices, journal notes, documents, SMS logs, questionnaire responses.',
    },
    country: 'IE',
    country_label: {
      da: 'Irland & Tyskland (EU)',
      en: 'Ireland & Germany (EU)',
    },
    legal_basis: {
      da: 'EØS — ingen overførselsmekanisme krævet.',
      en: 'EEA — no transfer mechanism required.',
    },
    systems: ['v2', 'pay', 'forms'],
    url: 'https://supabase.com/security',
  },
  {
    id: 'hetzner',
    name: 'Hetzner Online GmbH',
    parent_entity: 'Hetzner Online GmbH, Gunzenhausen, Tyskland',
    service: {
      da:
        'Coolify-værtsserver i Nürnberg, der hoster Clinio v2 web-app, ' +
        'Clinio Pay API (Express) og Clinio Forms statisk frontend (Caddy).',
      en:
        'Coolify host server in Nuremberg running the Clinio v2 web app, ' +
        'Clinio Pay API (Express) and Clinio Forms static frontend (Caddy).',
    },
    data: {
      da:
        'Nginx/Caddy adgangslog (kort levetid); transiente HTTP request-bodies i ' +
        'hukommelsen. Ingen sundhedsdata persisteres af hostingværten.',
      en:
        'nginx/Caddy access logs (short-lived); transient HTTP request bodies in ' +
        'memory. No health data is persisted by the hosting host.',
    },
    country: 'DE',
    country_label: { da: 'Tyskland (EU)', en: 'Germany (EU)' },
    legal_basis: {
      da: 'EØS — ingen overførselsmekanisme krævet.',
      en: 'EEA — no transfer mechanism required.',
    },
    systems: ['v2', 'pay', 'forms'],
    url: 'https://www.hetzner.com/legal/privacy-policy',
  },
  {
    id: 'stripe',
    name: 'Stripe Payments Europe, Ltd.',
    parent_entity:
      'Stripe Payments Europe, Ltd., 1 Grand Canal Street Lower, Dublin, Irland (EEA-enhed for europæisk behandling)',
    service: {
      da:
        'Kortbetalinger, MobilePay og Connect Standard onboarding for Clinio ' +
        'Pay. Stripe verificerer KYC og udsteder Connected Accounts.',
      en:
        'Card payments, MobilePay and Connect Standard onboarding for Clinio ' +
        'Pay. Stripe performs KYC and issues Connected Accounts.',
    },
    data: {
      da:
        'Klinikkens kontaktoplysninger (KYC), kortmærke + sidste 4 cifre + token ' +
        'pr. transaktion, kortholderens navn hvis tilgængeligt, dispute-tekst. ' +
        'Aldrig CPR. Aldrig patientnavn eller e-mail.',
      en:
        'Clinic contact details (KYC), card brand + last 4 + token per ' +
        'transaction, cardholder name if available, dispute text. ' +
        'Never CPR. Never patient name or email.',
    },
    country: 'IE',
    country_label: { da: 'Irland (EU)', en: 'Ireland (EU)' },
    legal_basis: {
      da: 'EØS — ingen overførselsmekanisme krævet.',
      en: 'EEA — no transfer mechanism required.',
    },
    systems: ['pay'],
    url: 'https://stripe.com/legal/dpa',
  },
  {
    id: 'resend',
    name: 'Resend, Inc.',
    parent_entity: 'Resend, Inc. (USA; sending-region Irland)',
    service: {
      da:
        'Transaktionel e-mail: invitationer, kvitteringer, spørgeskema-' +
        'invitationer, faktura-e-mails. Clinio v2 ejer Resend; Clinio Pay og ' +
        'Clinio Forms sender ikke e-mail.',
      en:
        'Transactional email: invitations, receipts, questionnaire invitations, ' +
        'invoice emails. Clinio v2 owns Resend; Clinio Pay and Clinio Forms ' +
        'do not send email.',
    },
    data: {
      da:
        'Modtagerens navn og e-mail, fagligt e-mail-emne og brødtekst (typisk ' +
        'aftaledato + kliniknavn). Metadata gemmes i USA. Afsendelse sker fra ' +
        'Irland. Aldrig CPR. Aldrig kliniske noter.',
      en:
        'Recipient name and email, business email subject and body (typically ' +
        'appointment date + clinic name). Metadata is stored in the USA. ' +
        'Sending is from Ireland. Never CPR. Never clinical notes.',
    },
    country: 'US',
    country_label: {
      da: 'USA (metadata); Irland (afsendelse)',
      en: 'USA (metadata); Ireland (sending)',
    },
    legal_basis: {
      da:
        'EU SCCs 2021/914 (Module 2 — controller-to-processor) + TIA. ' +
        'Afsendelse fra Irland; metadata-lagring i USA.',
      en:
        'EU SCCs 2021/914 (Module 2 \u2014 controller-to-processor) + TIA. ' +
        'Sending from Ireland; metadata storage in the USA.',
    },
    systems: ['v2'],
    url: 'https://resend.com/legal/dpa',
  },
  {
    id: 'gatewayapi',
    name: 'GatewayAPI ApS',
    parent_entity: 'GatewayAPI ApS, København, Danmark',
    service: {
      da:
        'SMS-afsendelse til patienter (aftaleminder, brugerdefinerede SMS). ' +
        'Anvender HMAC-signerede leveringsrapporter tilbage til Clinio v2.',
      en:
        'SMS delivery to patients (appointment reminders, custom SMS). Uses ' +
        'HMAC-signed delivery reports back to Clinio v2.',
    },
    data: {
      da:
        'Patientens telefonnummer (E.164), SMS-tekst (kliniker-skrevet), ' +
        'leveringsstatus. Aldrig CPR.',
      en:
        'Patient phone number (E.164), SMS text (clinician-written), delivery ' +
        'status. Never CPR.',
    },
    country: 'DK',
    country_label: { da: 'Danmark (EU)', en: 'Denmark (EU)' },
    legal_basis: {
      da: 'EØS — ingen overførselsmekanisme krævet.',
      en: 'EEA — no transfer mechanism required.',
    },
    systems: ['v2'],
    url: 'https://gatewayapi.com/legal/gdpr-dpa/',
  },
  {
    id: 'sentry',
    name: 'Sentry (Functional Software, Inc.) — hvis aktiveret',
    parent_entity:
      'Functional Software, Inc. (US-moderselskab; Sentry tilbyder EU-only datacenter mod tilvalg)',
    service: {
      da:
        'Fejlovervågning og stacktraces fra Edge Functions og web-frontend. ' +
        'Anvendes hvis aktiveret pr. miljø.',
      en:
        'Error monitoring and stack traces from Edge Functions and web frontend. ' +
        'Used if enabled per environment.',
    },
    data: {
      da:
        'Stacktraces, anmodnings-metadata (HTTP-metode, sti, status). Bruger-id ' +
        'kan inkluderes hvis konfigureret; ' +
        'CPR/patientnavne fjernes via konfigurerede scrubbers.',
      en:
        'Stack traces, request metadata (HTTP method, path, status). User id ' +
        'may be included if configured; ' +
        'CPR / patient names are stripped by configured scrubbers.',
    },
    country: 'EU',
    country_label: {
      da: 'EU (Sentry EU datacenter, afventer aktivering bekræftet af juridisk rådgiver)',
      en: 'EU (Sentry EU datacenter, pending activation confirmed by legal counsel)',
    },
    legal_basis: {
      da:
        'EU SCCs 2021/914 + TIA hvis ikke EU-only. Aktivér Sentrys EU-region ' +
        'før produktion (afventer bekræftelse).',
      en:
        'EU SCCs 2021/914 + TIA if not EU-only. Enable Sentry\u2019s EU region ' +
        'before production (pending confirmation).',
    },
    systems: ['v2', 'pay', 'forms'],
    url: 'https://sentry.io/legal/dpa/',
  },
  {
    id: 'whereby',
    name: 'Whereby AS — hvis aktiveret pr. klinik',
    parent_entity: 'Whereby AS, Norge (EØS)',
    service: {
      da:
        'Videoaftaler (eksternt embed) for klinikker, der har slået ' +
        'videoaftaler til. Video-streamen krypteres ende-til-ende mellem deltagere.',
      en:
        'Video appointments (external embed) for clinics that have enabled video ' +
        'appointments. Video stream is end-to-end encrypted between participants.',
    },
    data: {
      da:
        'Møde-URL (unguessable token), deltager-display-navn. Selve video- ' +
        'eller lyddata persisteres ikke af Whereby (medmindre klinikken aktiverer ' +
        'optagelse — pt. ikke understøttet i v2).',
      en:
        'Meeting URL (unguessable token), participant display name. Video and ' +
        'audio are not persisted by Whereby (unless the clinic enables ' +
        'recording \u2014 not supported in v2 today).',
    },
    country: 'NO',
    country_label: { da: 'Norge (EØS)', en: 'Norway (EEA)' },
    legal_basis: {
      da: 'EØS — ingen overførselsmekanisme krævet.',
      en: 'EEA — no transfer mechanism required.',
    },
    systems: ['v2'],
    url: 'https://whereby.com/information/tos/privacy-policy/',
  },
  {
    id: 'qualitymetric',
    name: 'QualityMetric / RAND (SF-36) — kun indhold, ikke databehandler',
    parent_entity: 'QualityMetric Incorporated / RAND Corporation (US) — indholdslicens for SF-36',
    service: {
      da:
        'Indholdslicens til SF-36 PROM. INGEN respondent-data sendes til ' +
        'licensgiveren; alene en platformlicens (pending bekræftelse pr. CLINIO-228).',
      en:
        'Content licence for the SF-36 PROM. NO respondent data is sent to the ' +
        'licensor; platform-level content licence only (pending confirmation per CLINIO-228).',
    },
    data: {
      da: 'Ingen persondata krydser til licensgiveren.',
      en: 'No personal data crosses to the licensor.',
    },
    country: 'US',
    country_label: { da: 'USA (kun indholdslicens)', en: 'USA (content licence only)' },
    legal_basis: {
      da: 'Ikke databehandler under GDPR.',
      en: 'Not a data processor under GDPR.',
    },
    systems: ['forms'],
    url: 'https://www.qualitymetric.com/',
  },
];

function iconForSystem(system: 'v2' | 'pay' | 'forms') {
  if (system === 'pay') return <CreditCard className="inline h-3 w-3" aria-hidden />;
  if (system === 'forms') return <Database className="inline h-3 w-3" aria-hidden />;
  return <Server className="inline h-3 w-3" aria-hidden />;
}

function iconForSubProcessor(id: string) {
  if (id === 'sentry') return <Bug className="h-5 w-5 text-ds-teal" aria-hidden />;
  if (id === 'resend') return <Mail className="h-5 w-5 text-ds-teal" aria-hidden />;
  if (id === 'stripe') return <CreditCard className="h-5 w-5 text-ds-teal" aria-hidden />;
  if (id === 'hetzner') return <Server className="h-5 w-5 text-ds-teal" aria-hidden />;
  if (id === 'supabase') return <Database className="h-5 w-5 text-ds-teal" aria-hidden />;
  return <MapPin className="h-5 w-5 text-ds-teal" aria-hidden />;
}

export function SubProcessors() {
  const [locale, setLocale] = useState<Locale>('da');
  const [systemFilter, setSystemFilter] = useState<'all' | 'v2' | 'pay' | 'forms'>('all');

  const filtered = useMemo(() => {
    if (systemFilter === 'all') return SUB_PROCESSORS;
    return SUB_PROCESSORS.filter((sp) => sp.systems.includes(systemFilter));
  }, [systemFilter]);

  const otherLocale: Locale = locale === 'da' ? 'en' : 'da';

  return (
    <div className="min-h-screen bg-ds-bg">
      {/* Hero */}
      <div className="bg-gradient-to-br from-ds-teal to-ds-teal-700">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-ds-on-accent/80">
                <Shield className="h-5 w-5" aria-hidden />
                <span className="text-sm uppercase tracking-wide">GDPR / Art. 28</span>
              </div>
              <h1 className="mt-2 text-3xl font-bold text-ds-on-accent sm:text-4xl">
                {COPY.page_title[locale]}
              </h1>
              <p className="mt-2 max-w-2xl text-base text-ds-on-accent/80">
                {COPY.page_subtitle[locale]}
              </p>
              <p className="mt-3 text-xs text-ds-on-accent/70">
                {COPY.last_reviewed[locale]}: <span className="font-mono">{LAST_REVIEWED}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => setLocale(otherLocale)}
              className="inline-flex shrink-0 items-center gap-2 rounded-md border border-ds-on-accent/40 bg-ds-card/10 px-3 py-2 text-sm font-medium text-ds-on-accent backdrop-blur-sm transition hover:bg-ds-card/20"
              aria-label={locale === 'da' ? 'Switch to English' : 'Skift til dansk'}
            >
              <Languages className="h-4 w-4" aria-hidden />
              {locale === 'da' ? 'English' : 'Dansk'}
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <main className="mx-auto max-w-6xl px-6 py-10">
        <p className="text-base text-ds-text">{COPY.preamble[locale]}</p>

        {/* Hard rules */}
        <section className="mt-8 rounded-lg border border-ds-amber-line bg-ds-amber-tint p-5">
          <h2 className="text-lg font-semibold text-ds-amber-ink">
            {COPY.hard_rules_title[locale]}
          </h2>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-ds-amber-ink">
            {COPY.hard_rules[locale].map((rule, idx) => {
              const isLast = idx === COPY.hard_rules[locale].length - 1;
              return (
                <li key={idx}>
                  {rule}
                  {isLast && ' ' + COPY.for_the_full_list[locale]}
                </li>
              );
            })}
          </ol>
        </section>

        {/* Filter */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="text-sm font-medium text-ds-text">
            {COPY.filter_label[locale]}
            <select
              value={systemFilter}
              onChange={(e) => setSystemFilter(e.target.value as typeof systemFilter)}
              className="ml-2 rounded-md border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text shadow-card focus:border-ds-teal focus:outline-none"
            >
              <option value="all">{COPY.filter_all[locale]}</option>
              <option value="v2">{COPY.systems_label.v2[locale]}</option>
              <option value="pay">{COPY.systems_label.pay[locale]}</option>
              <option value="forms">{COPY.systems_label.forms[locale]}</option>
            </select>
          </label>
          <p className="text-xs text-ds-muted">
            {filtered.length} / {SUB_PROCESSORS.length}
          </p>
        </div>

        {/* Table — md+ */}
        <div className="mt-4 hidden overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card md:block">
          <table className="min-w-full divide-y divide-ds-line">
            <thead className="bg-ds-sunk">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ds-muted"
                >
                  {COPY.table_header.sub_processor[locale]}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ds-muted"
                >
                  {COPY.table_header.service[locale]}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ds-muted"
                >
                  {COPY.table_header.data[locale]}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ds-muted"
                >
                  {COPY.table_header.country[locale]}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ds-muted"
                >
                  {COPY.table_header.legal_basis[locale]}
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ds-muted"
                >
                  {COPY.table_header.systems[locale]}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-line bg-ds-card text-sm">
              {filtered.map((sp) => (
                <tr key={sp.id} className="align-top">
                  <td className="px-4 py-4">
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5">{iconForSubProcessor(sp.id)}</span>
                      <div>
                        <div className="font-medium text-ds-text">{sp.name}</div>
                        <div className="mt-1 text-xs text-ds-muted">{sp.parent_entity}</div>
                        {sp.url && (
                          <a
                            href={sp.url}
                            className="mt-1 inline-block text-xs font-medium text-ds-teal underline-offset-2 hover:underline"
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {sp.url.replace(/^https?:\/\//, '')}
                          </a>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-ds-text">{sp.service[locale]}</td>
                  <td className="px-4 py-4 text-ds-text">{sp.data[locale]}</td>
                  <td className="px-4 py-4 text-ds-text">
                    <div className="flex items-center gap-1 font-medium">
                      <MapPin className="h-3.5 w-3.5 text-ds-muted" aria-hidden />
                      {sp.country_label[locale]}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-ds-text">{sp.legal_basis[locale]}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {sp.systems.map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-2 py-0.5 text-xs font-medium text-ds-teal"
                        >
                          {iconForSystem(s)}
                          {COPY.systems_label[s][locale]}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Cards — mobile */}
        <div className="mt-4 grid gap-4 md:hidden">
          {filtered.map((sp) => (
            <article
              key={sp.id}
              className="rounded-lg border border-ds-line bg-ds-card p-4 shadow-card"
            >
              <div className="flex items-start gap-2">
                {iconForSubProcessor(sp.id)}
                <div className="flex-1">
                  <h3 className="font-semibold text-ds-text">{sp.name}</h3>
                  <p className="mt-0.5 text-xs text-ds-muted">{sp.parent_entity}</p>
                </div>
              </div>
              <dl className="mt-3 space-y-2 text-sm">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
                    {COPY.table_header.service[locale]}
                  </dt>
                  <dd className="text-ds-text">{sp.service[locale]}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
                    {COPY.table_header.data[locale]}
                  </dt>
                  <dd className="text-ds-text">{sp.data[locale]}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
                    {COPY.table_header.country[locale]}
                  </dt>
                  <dd className="text-ds-text">{sp.country_label[locale]}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
                    {COPY.table_header.legal_basis[locale]}
                  </dt>
                  <dd className="text-ds-text">{sp.legal_basis[locale]}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
                    {COPY.table_header.systems[locale]}
                  </dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {sp.systems.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-2 py-0.5 text-xs font-medium text-ds-teal"
                      >
                        {iconForSystem(s)}
                        {COPY.systems_label[s][locale]}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>
            </article>
          ))}
        </div>

        {/* Contact + archive */}
        <section className="mt-10 rounded-lg border border-ds-line bg-ds-card p-5">
          <h2 className="text-lg font-semibold text-ds-text">{COPY.contact_title[locale]}</h2>
          <p className="mt-2 text-sm text-ds-text">
            {COPY.contact_body[locale]}
            <a
              href={`mailto:${COPY.contact_email}`}
              className="font-medium text-ds-teal underline-offset-2 hover:underline"
            >
              {COPY.contact_email}
            </a>
            .
          </p>
          <h3 className="mt-5 text-sm font-semibold text-ds-text">{COPY.archive_title[locale]}</h3>
          <p className="mt-1 text-sm text-ds-muted">{COPY.archive_body[locale]}</p>
        </section>

        <p className="mt-8 text-xs text-ds-muted">
          <Link to="/" className="underline-offset-2 hover:underline">
            {locale === 'da' ? 'Tilbage til Clinio' : 'Back to Clinio'}
          </Link>
        </p>
      </main>
    </div>
  );
}
